import { create } from 'zustand';

export interface AITokensState {
  // Token information
  availableTokens: number;
  usedTokens: number;
  monthlyAllowance: number;
  
  // Check status
  isLoading: boolean;
  hasChecked: boolean;
  isCheckingTokens: boolean;
  
  // Actions
  checkTokens: (userId: string) => Promise<boolean>;
  resetTokenState: () => void;
  setTokens: (tokens: {
    available_tokens?: number;
    used_tokens?: number;
    monthly_allowance?: number;
  }) => void;
}

export const useAITokensStore = create<AITokensState>((set, get) => ({
  // Initial state
  availableTokens: 0,
  usedTokens: 0,
  monthlyAllowance: 0,
  isLoading: false,
  hasChecked: false,
  isCheckingTokens: false,
  
  // Reset token state
  resetTokenState: () => set({
    availableTokens: 0,
    usedTokens: 0,
    monthlyAllowance: 0,
    hasChecked: false,
    isLoading: false,
    isCheckingTokens: false,
  }),
  
  // Set token information
  setTokens: (tokens) => set({
    ...(tokens.available_tokens !== undefined && { availableTokens: tokens.available_tokens }),
    ...(tokens.used_tokens !== undefined && { usedTokens: tokens.used_tokens }),
    ...(tokens.monthly_allowance !== undefined && { monthlyAllowance: tokens.monthly_allowance }),
    hasChecked: true,
  }),
  
  // Check available tokens
  checkTokens: async (userId: string) => {
    const state = get();
    
    // Don't check again if already checking to prevent multiple requests
    if (state.isCheckingTokens) {
      return state.availableTokens > 0;
    }
    
    try {
      set({ isCheckingTokens: true, isLoading: true });
      
      const response = await fetch(`/.netlify/functions/ai-check-usage?userId=${userId}`);
      
      if (!response.ok) {
        console.error('Error checking AI tokens:', response.statusText);
        return false;
      }
      
      const data = await response.json();
      
      set({
        availableTokens: data.available_tokens,
        usedTokens: data.used_tokens,
        monthlyAllowance: data.monthly_allowance,
        hasChecked: true,
        isLoading: false,
        isCheckingTokens: false,
      });
      
      // Return true if user has available tokens
      return data.available_tokens > 0;
    } catch (error) {
      console.error('Error checking AI tokens:', error);
      set({ isLoading: false, isCheckingTokens: false });
      return false;
    }
  }
})); 